<template>
  <section
    class="relative w-screen -mx-[calc(50vw-50%)] bg-zinc-50 text-zinc-900 dark:bg-black dark:text-zinc-50 overflow-hidden">
    <div class="absolute inset-0 pointer-events-none">
      <div class="absolute inset-0 transition-colors duration-500
               bg-[radial-gradient(1400px_700px_at_50%_-20%,rgba(56,189,248,.12),transparent_60%)]
               dark:bg-[radial-gradient(1400px_700px_at_50%_-20%,rgba(56,189,248,.18),transparent_60%)]"></div>
    </div>

    <div class="relative mx-auto max-w-6xl px-6 md:px-10 py-16 lg:py-24">
      <header class="text-center">
        <p class="text-[11px] uppercase tracking-[0.18em] text-zinc-500 dark:text-zinc-400">
          Color studio
        </p>
        <h2
          class="mt-3 text-3xl sm:text-4xl md:text-5xl font-semibold leading-[1.02] tracking-tight text-zinc-900 dark:text-zinc-50">
          Real palette workflows in NexGrid
        </h2>
        <p class="mt-4 max-w-2xl mx-auto text-sm sm:text-base text-zinc-600 dark:text-zinc-400">
          From brand systems to product themes, see how teams design, validate and ship color with
          confidence.
        </p>
      </header>

      <div class="mt-10 lg:mt-14 grid gap-10 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)] items-center"
        @mouseenter="pause" @mouseleave="resume">
        <div class="space-y-6">
          <div
            class="inline-flex items-center gap-2 rounded-full border border-zinc-200/70 dark:border-zinc-800/80 bg-white/60 dark:bg-white/5 px-3 py-1.5 text-[11px]">
            <span class="h-1.5 w-1.5 rounded-full" :style="{ backgroundColor: currentShot.tint }"></span>
            <span class="uppercase tracking-[0.16em] text-zinc-600 dark:text-zinc-400">
              {{ currentShot.badge }}
            </span>
          </div>

          <div class="space-y-3">
            <h3 class="text-2xl sm:text-3xl lg:text-[2.1rem] font-semibold leading-tight">
              {{ currentShot.title }}
            </h3>
            <p class="text-sm sm:text-[15px] leading-relaxed text-zinc-600 dark:text-zinc-400">
              {{ currentShot.subtitle }}
            </p>
          </div>

          <div class="flex flex-wrap gap-2 mt-1">
            <span v-for="chip in currentShot.points" :key="chip"
              class="rounded-full bg-zinc-900/5 px-3 py-1.5 text-[11px] font-medium text-zinc-700 dark:bg-zinc-50/5 dark:text-zinc-200">
              {{ chip }}
            </span>
          </div>

          <div
            class="mt-5 grid grid-cols-2 sm:flex sm:flex-wrap gap-x-8 gap-y-3 text-xs sm:text-[13px] text-zinc-500 dark:text-zinc-400">
            <div v-for="m in currentShot.metrics" :key="m.label" class="space-y-0.5">
              <div class="font-semibold text-zinc-900 dark:text-zinc-50">
                {{ m.value }}
              </div>
              <div>{{ m.label }}</div>
            </div>
          </div>

          <div
            class="mt-6 rounded-xl border border-dashed border-zinc-200 dark:border-zinc-800/80 bg-white/60 dark:bg-white/[0.02] px-4 py-3 text-[11px] flex flex-wrap items-center gap-3">
            <span class="uppercase tracking-[0.16em] text-zinc-500 dark:text-zinc-400">
              Tokens
            </span>
            <span class="h-px w-6 bg-zinc-200 dark:bg-zinc-800"></span>
            <span class="font-mono text-[11px] text-zinc-700 dark:text-zinc-300">
              {{ currentShot.tokens }}
            </span>
          </div>
        </div>

        <div class="relative">
          <div
            class="relative rounded-2xl border border-white/10 bg-zinc-950/95 text-zinc-50 shadow-[0_24px_80px_rgba(15,23,42,0.85)] overflow-hidden">
            <div class="absolute inset-0 opacity-80" :style="{
              background: `radial-gradient(circle at 0% 0%, ${currentShot.tint}44 0, transparent 55%), radial-gradient(circle at 100% 100%, #22c55e33 0, transparent 55%)`
            }"></div>

            <div class="relative p-5 sm:p-6 lg:p-7 space-y-6">
              <div class="flex items-center justify-between gap-3">
                <div>
                  <p class="text-[11px] uppercase tracking-[0.16em] text-zinc-400">
                    Palette preview
                  </p>
                  <p class="mt-1 text-sm font-medium text-zinc-50">
                    {{ currentShot.name }}
                  </p>
                </div>
                <div
                  class="inline-flex items-center gap-2 rounded-full bg-zinc-900/60 px-3 py-1.5 text-[11px] text-zinc-200 border border-white/10">
                  <span class="h-1.5 w-1.5 rounded-full bg-emerald-400"></span>
                  Live contrast
                </div>
              </div>

              <div class="space-y-4">
                <div class="flex gap-2">
                  <div v-for="c in currentShot.palette" :key="c"
                    class="flex-1 rounded-xl h-16 sm:h-18 border border-white/10" :style="{ backgroundColor: c }"></div>
                </div>

                <div class="grid grid-cols-4 gap-2">
                  <div v-for="c in currentShot.neutrals" :key="c.hex"
                    class="rounded-lg border border-white/5 bg-zinc-900/60 px-2.5 py-2 space-y-1">
                    <div class="flex items-center justify-between text-[10px] text-zinc-400">
                      <span>{{ c.role }}</span>
                      <span class="h-3 w-3 rounded-full border border-white/15"
                        :style="{ backgroundColor: c.hex }"></span>
                    </div>
                    <div class="font-mono text-[10px] text-zinc-200 truncate">
                      {{ c.hex }}
                    </div>
                  </div>
                </div>
              </div>

              <div class="rounded-xl border border-white/8 bg-zinc-900/80 px-3 py-3 text-[11px] space-y-2">
                <div class="flex items-center justify-between text-zinc-400">
                  <span>WCAG summary</span>
                  <span class="font-mono text-[10px] text-emerald-300">
                    {{ currentShot.wcagSummary }}
                  </span>
                </div>
                <div class="flex items-center gap-2 text-[10px] text-zinc-400">
                  <span v-for="tag in currentShot.wcagTags" :key="tag"
                    class="rounded-full bg-zinc-800/80 px-2 py-1 border border-white/5">
                    {{ tag }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <button @click="prev"
            class="hidden md:flex absolute -left-6 top-1/2 -translate-y-1/2 h-9 w-9 items-center justify-center rounded-full border border-zinc-200/70 bg-white/80 text-zinc-700 shadow-sm hover:bg-white hover:shadow-md dark:border-zinc-800 dark:bg-zinc-900/90 dark:text-zinc-100 dark:hover:bg-zinc-900 transition"
            aria-label="Previous">
            ‹
          </button>
          <button @click="next"
            class="hidden md:flex absolute -right-6 top-1/2 -translate-y-1/2 h-9 w-9 items-center justify-center rounded-full border border-zinc-200/70 bg-white/80 text-zinc-700 shadow-sm hover:bg-white hover:shadow-md dark:border-zinc-800 dark:bg-zinc-900/90 dark:text-zinc-100 dark:hover:bg-zinc-900 transition"
            aria-label="Next">
            ›
          </button>
        </div>
      </div>

      <div class="mt-10 flex flex-wrap items-center justify-center gap-3">
        <button v-for="(shot, i) in shots" :key="shot.id" @click="go(i)"
          class="flex items-center gap-2 rounded-full border px-3.5 py-1.5 text-[11px] transition-all" :class="i === active
              ? 'border-zinc-900 bg-zinc-900 text-zinc-50 dark:border-zinc-50 dark:bg-zinc-50 dark:text-zinc-900 shadow-sm'
              : 'border-zinc-200 bg-white/60 text-zinc-500 hover:text-zinc-900 hover:border-zinc-300 dark:border-zinc-800 dark:bg-zinc-900/60 dark:text-zinc-400 dark:hover:text-zinc-100'
            ">
          <span class="h-1.5 w-1.5 rounded-full" :style="{ backgroundColor: shot.tint }"></span>
          <span>{{ shot.label }}</span>
        </button>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'

const active = ref(0)
let autoplay

const shots = [
  {
    id: 'board',
    label: 'Palette board',
    name: 'Multi-channel board',
    title: 'One board, every palette you care about',
    subtitle:
      'Keep product, brand, and marketing palettes aligned in a single studio view that updates in real time.',
    badge: 'Studio overview',
    tint: '#0ea5e9',
    points: ['Brand vs product tracks', 'Campaign-specific palettes', 'Team-ready views'],
    metrics: [
      { label: 'Palettes tracked', value: '32' },
      { label: 'Teams connected', value: '7' },
      { label: 'Boards per brand', value: '3' }
    ],
    tokens: 'color.brand / color.product / color.marketing / color.signal',
    palette: ['#0F172A', '#E11D48', '#F97316', '#22C55E'],
    neutrals: [
      { role: 'Bg', hex: '#020617' },
      { role: 'Surface', hex: '#0B1120' },
      { role: 'Text', hex: '#F9FAFB' },
      { role: 'Muted', hex: '#64748B' }
    ],
    wcagSummary: 'AA large / AA normal',
    wcagTags: ['Headlines', 'Body', 'Buttons']
  },
  {
    id: 'contrast',
    label: 'Contrast lab',
    name: 'Light / dark lab',
    title: 'Contrast you can trust across themes',
    subtitle:
      'Preview headings, body and interactive states over light and dark surfaces with live WCAG scoring.',
    badge: 'Contrast testing',
    tint: '#4f46e5',
    points: ['Theme toggle', 'Role-based pairs', 'AA/AAA scoring'],
    metrics: [
      { label: 'Pairs tested', value: '540+' },
      { label: 'AA compliant', value: '96%' },
      { label: 'AAA candidates', value: '41' }
    ],
    tokens: 'fg.default / fg.muted / bg.canvas / accent.primary',
    palette: ['#020617', '#E5E7EB', '#4F46E5', '#FACC15'],
    neutrals: [
      { role: 'Canvas', hex: '#020617' },
      { role: 'Card', hex: '#020617' },
      { role: 'Text', hex: '#F9FAFB' },
      { role: 'Subtle', hex: '#94A3B8' }
    ],
    wcagSummary: 'AA / AAA per role',
    wcagTags: ['Dark mode', 'Light mode', 'States']
  },
  {
    id: 'tokens',
    label: 'Token grid',
    name: 'Semantic token grid',
    title: 'Tokens that stay in sync with design and code',
    subtitle:
      'Map semantic roles to real colors once, then export to JSON, CSS variables and your design tools.',
    badge: 'Design tokens',
    tint: '#22c55e',
    points: ['Role-first naming', 'Multi-export', 'Single source of truth'],
    metrics: [
      { label: 'Tokens active', value: '180+' },
      { label: 'Systems synced', value: '3' },
      { label: 'Export formats', value: '4' }
    ],
    tokens: 'color.success / color.info / color.warning / color.danger',
    palette: ['#22C55E', '#0EA5E9', '#F97316', '#EF4444'],
    neutrals: [
      { role: 'Success', hex: '#22C55E' },
      { role: 'Info', hex: '#0EA5E9' },
      { role: 'Warn', hex: '#F97316' },
      { role: 'Error', hex: '#EF4444' }
    ],
    wcagSummary: 'AA on dark / light',
    wcagTags: ['System states', 'Alerts', 'Feedback']
  },
  {
    id: 'brands',
    label: 'Brand kits',
    name: 'Brand kit builder',
    title: 'Brand palettes ready for every touchpoint',
    subtitle:
      'Generate full brand kits with primaries, neutrals and accents tuned for UI, print and marketing.',
    badge: 'Brand systems',
    tint: '#ec4899',
    points: ['Primary + neutrals', 'Accent logic', 'UI-safe output'],
    metrics: [
      { label: 'Brand kits', value: '24' },
      { label: 'Variants each', value: '3–5' },
      { label: 'Exports per kit', value: '120+' }
    ],
    tokens: 'brand.primary / brand.neutral / brand.accent / brand.overlay',
    palette: ['#020617', '#F9FAFB', '#EC4899', '#F97316'],
    neutrals: [
      { role: 'Primary', hex: '#EC4899' },
      { role: 'Accent', hex: '#F97316' },
      { role: 'Bg', hex: '#020617' },
      { role: 'On-bg', hex: '#F9FAFB' }
    ],
    wcagSummary: 'AA for UI usage',
    wcagTags: ['Brand', 'Landing pages', 'Hero']
  }
]

const currentShot = computed(() => shots[active.value])

const next = () => {
  active.value = (active.value + 1) % shots.length
}

const prev = () => {
  active.value = (active.value - 1 + shots.length) % shots.length
}

const go = i => {
  active.value = i
}

const play = () => {
  autoplay = setInterval(next, 8000)
}

const pause = () => {
  if (autoplay) clearInterval(autoplay)
}

const resume = () => {
  if (!autoplay) play()
}

onMounted(play)
onBeforeUnmount(pause)
</script>
